import { BuildsApiError, events as getEvents, get as getRun, } from './client';
import { TERMINAL_STATES } from './types';
const FAST_POLL_MS = 1000;
const NORMAL_POLL_MS = 2000;
const PARKED_POLL_MS = 5000;
const LONG_RUNNING_POLL_MS = 10000;
const THIRTY_SECONDS_MS = 30000;
const TWO_MINUTES_MS = 2 * 60000;
const TEN_MINUTES_MS = 10 * 60000;
const terminalStates = new Set(TERMINAL_STATES);
function pollCadence(state, elapsedMs) {
    if (elapsedMs >= TEN_MINUTES_MS)
        return LONG_RUNNING_POLL_MS;
    if ((state === null || state === void 0 ? void 0 : state.startsWith('parked')) || elapsedMs >= TWO_MINUTES_MS)
        return PARKED_POLL_MS;
    // The first 30s is where the user is staring at "waiting for a build slot" /
    // "building" and every transition matters most; poll fast, then relax.
    if (elapsedMs < THIRTY_SECONDS_MS)
        return FAST_POLL_MS;
    return NORMAL_POLL_MS;
}
function retryCadence(error, state, elapsedMs) {
    const cadence = pollCadence(state, elapsedMs);
    if (error instanceof BuildsApiError && error.status === 429 && error.retryAfterMs !== undefined) {
        return Math.max(cadence, error.retryAfterMs);
    }
    return cadence;
}
function hasRetryAfter(error) {
    return error instanceof BuildsApiError
        && error.status === 429
        && error.retryAfterMs !== undefined;
}
// A permanent API refusal (expired session, revoked access, unknown run) will
// never heal by re-polling; the loop must stop and surface it instead of
// retrying forever. 429 stays retryable regardless of the server envelope.
function isPermanentError(error) {
    return error instanceof BuildsApiError
        && error.status !== 429
        && error.retryable === false;
}
function newEvents(rows, lastSeq, seen) {
    const cursorBeforePoll = lastSeq;
    const fresh = [];
    let nextCursor = lastSeq;
    for (const event of rows) {
        if (!Number.isFinite(event === null || event === void 0 ? void 0 : event.seq))
            continue;
        nextCursor = Math.max(nextCursor, event.seq);
        if (event.seq <= cursorBeforePoll || seen.has(event.seq))
            continue;
        seen.add(event.seq);
        fresh.push(event);
    }
    return { events: fresh, lastSeq: nextCursor };
}
/**
 * Poll a build view and its event cursor until stopped or the run reaches a
 * known terminal state. Unknown state strings remain untouched and nonterminal.
 */
export function watch(appId, runId, onUpdate, options = {}) {
    const startedAt = Date.now();
    const seenSeqs = new Set();
    let lastSeq = Number.isFinite(options.after)
        ? Math.max(0, Math.floor(options.after))
        : 0;
    let timer;
    let active = true;
    let lastState;
    const stop = () => {
        active = false;
        if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
        }
    };
    const schedule = (delayMs) => {
        if (!active)
            return;
        timer = setTimeout(() => {
            timer = undefined;
            void poll();
        }, delayMs);
    };
    const reportError = (error) => {
        var _a;
        try {
            (_a = options.onError) === null || _a === void 0 ? void 0 : _a.call(options, error);
        }
        catch (_b) {
            // A consumer error callback must not break the transport loop.
        }
    };
    const publish = (run, events) => {
        try {
            onUpdate({ run, events });
        }
        catch (_a) {
            // Polling is transport state; consumer render errors must not strand it.
        }
    };
    const poll = async () => {
        if (!active)
            return;
        // Fire the view and the events page concurrently: the events cursor (lastSeq)
        // does not depend on the run view, so there is no reason to pay two serial
        // round-trips per tick. Error semantics below are unchanged — the run result
        // still gates, and a failed events page still publishes the run with no rows.
        const runPromise = getRun(appId, runId);
        const eventsPromise = getEvents(appId, runId, lastSeq);
        // Keep the in-flight events page from becoming an unhandled rejection if the
        // run result makes us bail before awaiting it; the real await below still sees it.
        eventsPromise.catch(() => { });
        let run;
        try {
            const view = await runPromise;
            if (!active)
                return;
            run = view.run;
            lastState = run.state;
        }
        catch (error) {
            if (!active)
                return;
            reportError(error);
            if (isPermanentError(error)) {
                stop();
                return;
            }
            schedule(retryCadence(error, lastState, Date.now() - startedAt));
            return;
        }
        try {
            const response = await eventsPromise;
            if (!active)
                return;
            const deduped = newEvents(Array.isArray(response.events) ? response.events : [], lastSeq, seenSeqs);
            lastSeq = deduped.lastSeq;
            publish(run, deduped.events);
        }
        catch (error) {
            if (!active)
                return;
            // The view still advances UI state even when event replay is rate-limited.
            publish(run, []);
            reportError(error);
            if (terminalStates.has(run.state)) {
                // The run itself is done, but a rate-limited final event replay still
                // owes the caller its unseen seq rows. Honor Retry-After, retry the
                // piggyback once the limiter permits it, then stop after a successful
                // replay. Other terminal replay failures stop normally.
                if (hasRetryAfter(error)) {
                    schedule(retryCadence(error, run.state, Date.now() - startedAt));
                    return;
                }
                stop();
                return;
            }
            if (isPermanentError(error)) {
                stop();
                return;
            }
            schedule(retryCadence(error, run.state, Date.now() - startedAt));
            return;
        }
        if (!active)
            return;
        if (terminalStates.has(run.state)) {
            // Drain the remaining event pages before stopping: a resumed terminal
            // watch may owe the caller more rows than one page returns. Each page
            // advances the cursor, so the loop terminates; the cap is a backstop.
            for (let page = 0; page < 50 && active; page += 1) {
                let drained;
                try {
                    const response = await getEvents(appId, runId, lastSeq);
                    if (!active)
                        return;
                    drained = newEvents(Array.isArray(response.events) ? response.events : [], lastSeq, seenSeqs);
                }
                catch (error) {
                    reportError(error);
                    break;
                }
                if (drained.events.length === 0)
                    break;
                lastSeq = drained.lastSeq;
                publish(run, drained.events);
            }
            stop();
            return;
        }
        schedule(pollCadence(run.state, Date.now() - startedAt));
    };
    void poll();
    return { stop };
}
